import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, getContentPlanDraft, getQuickstartPlanStatus, refreshContentPlanSerp } from '../../services/api';
import type { Project } from '../../services/api';
import SchedulingCalendar from './SchedulingCalendar';
import PlatformCredentials from './PlatformCredentials';
import { GlassCard } from '../ui/GlassCard';
import { GlassTabs } from '../ui/GlassTabs';
import { ModuleHeader } from '../ui/ModuleHeader';
import { GlassButton } from '../ui/GlassButton';
import ContentPlanWizard from '../SEO/ContentPlanWizard';
import { usePermissions } from '../../hooks/usePermissions';
import { Calendar, KeyRound, CalendarRange, AlertTriangle, RefreshCw, X } from 'lucide-react';
import './SchedulingPage.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


type TabType = 'calendar' | 'credentials';

const SchedulingPage: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<Project | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('calendar');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [wizardOpen, setWizardOpen] = useState(false);
    const { canAccessModule } = usePermissions();
    const hasSeo = canAccessModule('seo');

    // SERP refresh banner state
    const [lowConfidenceDraft, setLowConfidenceDraft] = useState(false);
    const [lowConfidenceItemCount, setLowConfidenceItemCount] = useState(0);
    const [serpRefreshing, setSerpRefreshing] = useState(false);
    const [serpRefreshToast, setSerpRefreshToast] = useState<string | null>(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

    useEffect(() => {
        if (projectId) {
            loadProject();
        }
    }, [projectId]);

    const loadProject = async () => {
        if (!projectId) return;

        try {
            setLoading(true);
            setError(null);
            const projectData = await getProject(projectId);
            setProject(projectData);

            // Check for low-confidence approved draft — triggers SERP refresh banner.
            // Try the seo-gated endpoint first (Growth+ users); fall back to the
            // onboarding quickstart-plan-status endpoint (no module gate, read-only)
            // so trial users still see the banner with an upgrade-prompt CTA.
            try {
                const draft = await getContentPlanDraft(projectId);
                if (draft && draft.approved_at && draft.low_confidence) {
                    setLowConfidenceDraft(true);
                    setLowConfidenceItemCount(draft.items?.length || 0);
                } else {
                    setLowConfidenceDraft(false);
                }
            } catch {
                // Seo endpoint 403/404 for trial users — fall back to the lightweight
                // status endpoint (no module gate) so the banner is still visible.
                try {
                    const status = await getQuickstartPlanStatus(projectId);
                    if (status.has_draft && status.approved_at && status.low_confidence) {
                        setLowConfidenceDraft(true);
                        // Item count unknown from status endpoint — use 0 so the confirm
                        // dialog shows "0 rows" (safe fallback; accurate count requires the
                        // full seo-gated draft endpoint which trial users cannot call).
                        setLowConfidenceItemCount(0);
                    } else {
                        setLowConfidenceDraft(false);
                    }
                } catch {
                    // Both failed — banner simply doesn't show (non-fatal)
                }
            }
        } catch (err: any) {
            console.error('Failed to load project:', err);
            setError(getApiError(err, 'Failed to load project'));
        } finally {
            setLoading(false);
        }
    };

    const handleSerpRefresh = async () => {
        if (!projectId) return;
        if (!hasSeo) {
            setShowUpgradePrompt(true);
            return;
        }
        if (!window.confirm(
            `Refresh ${lowConfidenceItemCount} rows with live SERP data? This will use approximately ${lowConfidenceItemCount} SEO credits.`
        )) return;

        setSerpRefreshing(true);
        try {
            const result = await refreshContentPlanSerp(projectId);
            setLowConfidenceDraft(false);
            setSerpRefreshToast(`Refreshed ${result.refreshed} rows with live SERP data.`);
            setTimeout(() => setSerpRefreshToast(null), 5000);
        } catch (err: any) {
            const msg = err?.response?.data?.detail || 'SERP refresh failed. Please try again.';
            setSerpRefreshToast(typeof msg === 'string' ? msg : 'SERP refresh failed.');
            setTimeout(() => setSerpRefreshToast(null), 5000);
        } finally {
            setSerpRefreshing(false);
        }
    };

    if (loading) {
        return (
            <div className="scheduling-page">
                <GlassCard variant="default" padding="lg" className="loading-card">
                    <div className="loading">Loading project...</div>
                </GlassCard>
            </div>
        );
    }

    if (error) {
        return (
            <div className="scheduling-page">
                <GlassCard variant="default" padding="lg" className="error-card">
                    <div className="error-message">
                        <strong>Error:</strong> {error}
                    </div>
                    <GlassButton 
                        variant="primary" 
                        onClick={() => navigate('/projects')}
                        style={{ marginTop: '1rem' }}
                    >
                        Go to Projects
                    </GlassButton>
                </GlassCard>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="scheduling-page">
                <GlassCard variant="default" padding="lg" className="error-card">
                    <div className="error-message">
                        Project not found. Please select a project from the dashboard.
                    </div>
                    <GlassButton 
                        variant="primary" 
                        onClick={() => navigate('/projects')}
                        style={{ marginTop: '1rem' }}
                    >
                        Go to Projects
                    </GlassButton>
                </GlassCard>
            </div>
        );
    }

    const tabs = [
        { id: 'calendar', label: 'Calendar View', icon: <Calendar size={16} /> },
        { id: 'credentials', label: 'Platform Credentials', icon: <KeyRound size={16} /> },
    ];

    const headerActions: React.ReactNode[] = hasSeo
        ? [
              <GlassButton
                  key="generate-plan"
                  variant="secondary"
                  size="sm"
                  onClick={() => setWizardOpen(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                  <CalendarRange size={14} />
                  Generate 30-day plan
              </GlassButton>,
          ]
        : [];

    return (
        <div className="scheduling-page">
            <ModuleHeader
                title="Content Scheduling"
                breadcrumb={['Projects', project.name]}
                actions={headerActions.length > 0 ? headerActions : undefined}
            />

            {/* SERP refresh banner — shown when plan is low-confidence */}
            {lowConfidenceDraft && (
                <div className="scheduling-serp-banner">
                    <AlertTriangle size={16} className="scheduling-serp-banner-icon" />
                    <span className="scheduling-serp-banner-text">
                        This calendar was generated without live competitor data.
                        Refresh with live SERP for higher-confidence pillars and difficulty scores.
                    </span>
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={handleSerpRefresh}
                        disabled={serpRefreshing}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '12px', flexShrink: 0 }}
                    >
                        {serpRefreshing ? (
                            <><RefreshCw size={14} className="scheduling-spin" /> Refreshing…</>
                        ) : (
                            <><RefreshCw size={14} /> Refresh with live SERP data</>
                        )}
                    </GlassButton>
                </div>
            )}

            {/* Upgrade prompt for trial users */}
            {showUpgradePrompt && (
                <div className="scheduling-serp-banner scheduling-serp-upgrade">
                    <span>Upgrade to Growth to refresh your content plan with live SEO data.</span>
                    <GlassButton
                        variant="primary"
                        size="sm"
                        onClick={() => navigate('/profile')}
                        style={{ marginLeft: '12px', flexShrink: 0 }}
                    >
                        Upgrade
                    </GlassButton>
                    <button
                        onClick={() => setShowUpgradePrompt(false)}
                        style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Toast */}
            {serpRefreshToast && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '24px',
                        right: '24px',
                        zIndex: 99999,
                        padding: '12px 20px',
                        background: 'var(--color-success, #1eaca6)',
                        color: '#fff',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        fontSize: 'var(--text-sm)',
                        maxWidth: '360px',
                    }}
                >
                    {serpRefreshToast}
                </div>
            )}

            <div className="scheduling-tabs-container">
                <GlassTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={(tabId) => setActiveTab(tabId as TabType)}
                />
            </div>

            <GlassCard variant="default" padding="none" className="scheduling-content-card">
                <div className="scheduling-content">
                    {activeTab === 'calendar' && (
                        <SchedulingCalendar projectId={projectId!} />
                    )}
                    {activeTab === 'credentials' && (
                        <PlatformCredentials projectId={projectId!} />
                    )}
                </div>
            </GlassCard>

            {hasSeo && projectId && (
                <ContentPlanWizard
                    isOpen={wizardOpen}
                    onClose={() => setWizardOpen(false)}
                    projectId={projectId}
                />
            )}
        </div>
    );
};

export default SchedulingPage;
